import type { ColdEmailStatus, ColdEmailRecipientStatus } from "./cold-email-api";

type ChipColor = "default" | "primary" | "secondary" | "success" | "error" | "info" | "warning";

/** Human label + MUI Chip color for a batch status. */
export function coldStatusChip(status: ColdEmailStatus): { label: string; color: ChipColor } {
  switch (status) {
    case "pending_generation":
      return { label: "Awaiting draft", color: "default" };
    case "generating":
      return { label: "Drafting…", color: "info" };
    case "pending_review":
      return { label: "Needs review", color: "warning" };
    case "approved":
      return { label: "Approved", color: "info" };
    case "sending":
      return { label: "Sending", color: "info" };
    case "sent":
      return { label: "Sent", color: "success" };
    case "failed":
      return { label: "Failed", color: "error" };
    case "skipped":
      return { label: "Skipped", color: "default" };
    default:
      return { label: status, color: "default" };
  }
}

/**
 * Ensure the editor always shows the WHOLE email — greeting to signature — even
 * for a body drafted before that format (or one an operator trimmed). Prepends
 * the `{{full_name}}` greeting if missing, appends the CEO signature if missing.
 * A no-op for a body that already has both (new drafts), and idempotent, so it
 * is safe to run every time a draft loads. Mirrors ColdEmailMailer#full_email.
 */
const COLD_SIGNATURE = [
  "Warm regards,",
  "Sudhir Gupta",
  "CEO, XOP Networks",
  "",
  "https://www.xopnetworks.com",
  "O: 972 590 0201",
  "C: 214 564 2263",
  "Whatsapp: +1 214 564 2263",
  "XOP LinkedIn: https://www.linkedin.com/company/xopnetworks",
].join("\n");
export function ensureFullBody(body: string | null): string {
  const b = (body ?? "").trim();
  if (!b) return b;
  const hasGreeting =
    /\{\{\s*(?:full_name|first_name|name|recipient_name)\s*\}\}/i.test(b) ||
    /^(?:hello|hi|dear|greetings)\b/i.test(b);
  const hasSignature = /sudhir gupta/i.test(b);
  let out = b;
  if (!hasGreeting) out = `Hello {{full_name}},\n\n${out}`;
  if (!hasSignature) out = `${out}\n\n${COLD_SIGNATURE}`;
  return out;
}

export function coldRecipientChip(status: ColdEmailRecipientStatus): { label: string; color: ChipColor } {
  switch (status) {
    case "pending":
      return { label: "Pending", color: "default" };
    case "sent":
      return { label: "Sent", color: "success" };
    case "failed":
      return { label: "Failed", color: "error" };
    case "skipped":
      return { label: "Skipped", color: "warning" };
    default:
      return { label: status, color: "default" };
  }
}
